import { useState, useEffect, useMemo } from 'react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input, SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import {
  useOpportunities, useCreateOpportunity, useUpdateOpportunity, useDeleteOpportunity,
  useMoveOpportunityStage,
} from '@/hooks/useOpportunities'
import { useLeads, useCompanyContacts, useCreateContact } from '@/hooks/useLeads'
import { useCatalogItems } from '@/hooks/useCatalog'
import { useUsers } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { formatDate, formatCurrency } from '@/lib/utils'
import type {
  Opportunity, OpportunityCreate, OpportunityUpdate, OpportunityStage,
  Lead, Contact, ContactCreate, ContactRole, CatalogItem,
} from '@/types'
import { CONTACT_ROLE_LABELS } from '@/types'
import {
  Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, Eye,
  Target, TrendingUp, DollarSign, Building2, Search, UserPlus,
  Loader2, Lightbulb,
} from 'lucide-react'

// ==========================================
// Constants
// ==========================================

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string }, status?: number } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  if (axiosErr?.response?.status === 401) return 'Sessao expirada. Faca login novamente.'
  return fallback
}

const STAGE_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline' }> = {
  qualified: { label: 'Qualificado', variant: 'success' },
  proposal_sent: { label: 'Proposta Enviada', variant: 'warning' },
  negotiation: { label: 'Negociacao', variant: 'info' },
  closing: { label: 'Fechamento', variant: 'default' },
  won: { label: 'Ganho', variant: 'success' },
  lost: { label: 'Perdido', variant: 'destructive' },
}

const ACTIVE_STAGES: OpportunityStage[] = ['qualified', 'proposal_sent', 'negotiation', 'closing']

// ==========================================
// StatCard
// ==========================================

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg"><Icon className="h-5 w-5 text-primary" /></div>
          <div><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-bold">{value}</div></div>
        </div>
      </CardContent>
    </Card>
  )
}

// ==========================================
// Main Page
// ==========================================

export function OpportunitiesPage() {
  // Filtros top-level persistidos em sessionStorage. Volta da view detalhada
  // mantém o filtro; reseta no próximo browser session.
  const OPP_SS_KEY = 'opp-page:filters'
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState<string>(() => {
    try { return JSON.parse(sessionStorage.getItem(OPP_SS_KEY) || '{}').search || '' } catch { return '' }
  })
  const [filterStage, setFilterStage] = useState<OpportunityStage | ''>(() => {
    try { return (JSON.parse(sessionStorage.getItem(OPP_SS_KEY) || '{}').filterStage || '') as OpportunityStage | '' } catch { return '' }
  })
  const [filterOwner, setFilterOwner] = useState<string>(() => {
    try { return JSON.parse(sessionStorage.getItem(OPP_SS_KEY) || '{}').filterOwner || '' } catch { return '' }
  })
  useEffect(() => {
    try { sessionStorage.setItem(OPP_SS_KEY, JSON.stringify({ search, filterStage, filterOwner })) } catch {}
  }, [search, filterStage, filterOwner])
  const [showForm, setShowForm] = useState(false)
  const [editOpp, setEditOpp] = useState<Opportunity | null>(null)
  const [viewOpp, setViewOpp] = useState<Opportunity | null>(null)
  const [deleteOpp, setDeleteOpp] = useState<Opportunity | null>(null)

  const { data, isLoading } = useOpportunities({
    page: 1,
    perPage: 500,
  })
  const { data: usersData } = useUsers({ perPage: 100, isActive: true })

  const allOpps = data?.items || []
  const opps = useMemo(() => {
    let items = allOpps
    if (search) { const s = search.toLowerCase(); items = items.filter(o => o.name.toLowerCase().includes(s) || (o.lead_company_name || '').toLowerCase().includes(s)) }
    if (filterStage) items = items.filter(o => o.stage === filterStage)
    if (filterOwner) items = items.filter(o => o.owner_id === filterOwner)
    return items
  }, [allOpps, search, filterStage, filterOwner])
  const total = opps.length

  // Stats
  const totalValue = opps.reduce((s, o) => s + o.value, 0)
  const avgProb = opps.length ? Math.round(opps.reduce((s, o) => s + o.probability, 0) / opps.length) : 0

  return (
    <>
      
      <div className="p-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard icon={Lightbulb} label="Total" value={total} />
          <StatCard icon={DollarSign} label="Valor Total" value={formatCurrency(totalValue)} />
          <StatCard icon={Target} label="Prob. Media" value={`${avgProb}%`} />
          <StatCard icon={TrendingUp} label="Em Negociacao" value={opps.filter(o => o.stage === 'negotiation').length} />
        </div>

        {/* Filters + Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <SearchInput placeholder="Buscar oportunidade..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} onClear={() => setSearch('')} />
              </div>
              <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterStage} onChange={(e) => { setFilterStage(e.target.value as OpportunityStage | ''); setPage(1) }}>
                <option value="">Todos os stages</option>
                {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={filterOwner}
                onChange={(e) => { setFilterOwner(e.target.value); setPage(1) }}
                title="Filtrar por responsável"
              >
                <option value="">Todos os responsáveis</option>
                {(usersData?.items || []).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
              <Button onClick={() => { setEditOpp(null); setShowForm(true) }}><Plus className="h-4 w-4 mr-2" />Nova Oportunidade</Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <DataGrid
              gridId="opportunities"
              columns={[
                { id: 'name', header: 'Nome', accessor: (o: Opportunity) => <span className="font-medium">{o.name}</span>, sortValue: (o) => o.name, filterValue: (o) => o.name },
                { id: 'company', header: 'Empresa', accessor: (o: Opportunity) => <span className="text-muted-foreground">{o.lead_company_name || '-'}</span>, sortValue: (o) => o.lead_company_name || '', filterValue: (o) => o.lead_company_name || '' },
                { id: 'stage', header: 'Stage', accessor: (o: Opportunity) => <Badge variant={STAGE_CONFIG[o.stage]?.variant || 'secondary'}>{STAGE_CONFIG[o.stage]?.label || o.stage}</Badge>, sortValue: (o) => o.stage, filterValue: (o) => STAGE_CONFIG[o.stage]?.label || o.stage },
                { id: 'value', header: 'Valor (R$)', accessor: (o: Opportunity) => <span className="font-semibold">{formatCurrency(o.value)}</span>, sortValue: (o) => o.value, filterValue: (o) => formatCurrency(o.value), align: 'right' as const },
                { id: 'probability', header: 'Prob.', accessor: (o: Opportunity) => <span>{o.probability}%</span>, sortValue: (o) => o.probability, filterValue: (o) => o.probability + '%', align: 'center' as const },
                { id: 'owner', header: 'Responsavel', accessor: (o: Opportunity) => <span className="text-muted-foreground">{o.owner_name || '-'}</span>, sortValue: (o) => o.owner_name || '', filterValue: (o) => o.owner_name || '' },
                { id: 'close_date', header: 'Data Prevista', accessor: (o: Opportunity) => <span className="text-muted-foreground">{o.expected_close_date ? formatDate(o.expected_close_date) : '-'}</span>, sortValue: (o) => o.expected_close_date ? new Date(o.expected_close_date).getTime() : 0, filterValue: (o) => o.expected_close_date ? formatDate(o.expected_close_date) : '' },
                { id: 'actions', header: 'Acoes', sortable: false, filterable: false, align: 'center' as const, accessor: (o: Opportunity) => <div className="flex justify-center gap-1" onClick={(e: any) => e.stopPropagation()}><Button variant="ghost" size="icon" onClick={() => setViewOpp(o)}><Eye className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => { setEditOpp(o); setShowForm(true) }}><Edit2 className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => setDeleteOpp(o)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div> },
              ] as DataGridColumn<Opportunity>[]}
              data={opps}
              isLoading={isLoading}
              onRowClick={(o) => window.open(`/opportunities/${o.id}`, '_blank', 'noopener,noreferrer')}
              emptyMessage="Nenhuma oportunidade encontrada"
              emptyIcon={<Lightbulb className="h-12 w-12 opacity-30" />}
              pageSize={20}
            />
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {showForm && <OppFormModal opp={editOpp} onClose={() => setShowForm(false)} />}
      {viewOpp && <OppDetailModal opp={viewOpp} onClose={() => setViewOpp(null)} />}
      {deleteOpp && <DeleteOppModal opp={deleteOpp} onClose={() => setDeleteOpp(null)} />}
    </>
  )
}

// ==========================================
// Opportunity Form Modal
// ==========================================

function OppFormModal({ opp, onClose }: { opp: Opportunity | null; onClose: () => void }) {
  const isEdit = !!opp
  const createMutation = useCreateOpportunity()
  const updateMutation = useUpdateOpportunity()
  const moveStageMutation = useMoveOpportunityStage()
  const currentUser = useAuthStore((s) => s.user)
  const canChangeOwner = (currentUser?.role?.hierarchy_level ?? 0) >= 70

  // Form state
  const [form, setForm] = useState({
    name: opp?.name || '',
    lead_id: opp?.lead_id || '',
    contact_id: opp?.contact_id || '',
    value: opp?.value?.toString() || '',
    probability: opp?.probability?.toString() || '50',
    expected_close_date: opp?.expected_close_date || '',
    owner_id: opp?.owner_id || currentUser?.id || '',
    stage: opp?.stage || 'qualified' as OpportunityStage,
    product_text: '',
  })
  const [error, setError] = useState('')
  const [leadSearch, setLeadSearch] = useState('')
  const [showLeadDropdown, setShowLeadDropdown] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)

  // Data
  const { data: leadsData } = useLeads({ search: leadSearch, perPage: 10 })
  const leads = leadsData?.items || []
  const { data: contacts } = useCompanyContacts(selectedLead?.company_id || opp?.lead_company_id || null)
  const { data: usersData } = useUsers({ perPage: 100, isActive: true })
  const users = usersData?.items || []
  const { data: catalogData } = useCatalogItems()
  const catalogItems = catalogData?.items || []
  const createContactMutation = useCreateContact()

  // If editing, try to find the lead info from the leads list
  useEffect(() => {
    if (opp && opp.lead_id && !selectedLead) {
      // We have lead_company_name from the opp
      setLeadSearch('')
    }
  }, [opp, selectedLead])

  const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"

  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead)
    setForm({ ...form, lead_id: lead.id, contact_id: lead.primary_contact_id || '' })
    if (!form.name && lead.company?.name) {
      setForm(prev => ({ ...prev, lead_id: lead.id, contact_id: lead.primary_contact_id || '', name: lead.company?.name || '' }))
    }
    setShowLeadDropdown(false)
    setLeadSearch('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.lead_id) { setError('Selecione um Lead'); return }
    if (!form.name) { setError('Informe o nome da oportunidade'); return }
    if (!form.value || parseFloat(form.value) <= 0) { setError('Informe o valor estimado'); return }

    try {
      if (isEdit && opp) {
        const updateData: OpportunityUpdate = {
          name: form.name,
          value: parseFloat(form.value),
          probability: parseInt(form.probability) || 50,
          expected_close_date: form.expected_close_date || null,
          owner_id: form.owner_id || null,
          contact_id: form.contact_id || null,
        }
        await updateMutation.mutateAsync({ oppId: opp.id, data: updateData })
        // Mudanca de status (ex.: marcar como Ganho) usa o endpoint /stage,
        // que ajusta won_at/lost_at, probabilidade e o status do lead.
        if (form.stage !== opp.stage) {
          await moveStageMutation.mutateAsync({ oppId: opp.id, stage: form.stage })
        }
      } else {
        const createData: OpportunityCreate = {
          lead_id: form.lead_id,
          name: form.name,
          value: parseFloat(form.value),
          probability: parseInt(form.probability) || 50,
          expected_close_date: form.expected_close_date || null,
          owner_id: form.owner_id || null,
          contact_id: form.contact_id || null,
          stage: form.stage,
        }
        const created = await createMutation.mutateAsync(createData)
        // Se a oportunidade ja nasce Ganha/Perdida, dispara os efeitos de /stage.
        if (created && (form.stage === 'won' || form.stage === 'lost')) {
          await moveStageMutation.mutateAsync({ oppId: created.id, stage: form.stage })
        }
      }
      onClose()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar oportunidade'))
    }
  }

  // Contact form state
  const [contactForm, setContactForm] = useState<ContactCreate>({
    company_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    role: null,
    is_primary: false,
  })

  const handleCreateContact = async () => {
    if (!selectedLead?.company_id && !opp?.lead_company_id) return
    try {
      const newContact = await createContactMutation.mutateAsync({
        ...contactForm,
        company_id: selectedLead?.company_id || opp?.lead_company_id || "",
      })
      setForm({ ...form, contact_id: newContact.id })
      setShowContactForm(false)
      setContactForm({ company_id: '', first_name: '', last_name: '', email: '', phone: '', job_title: '', role: null, is_primary: false })
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao criar contato'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Oportunidade' : 'Nova Oportunidade'}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}

          {/* Lead Selection */}
          {!isEdit && (
            <div>
              <label className="text-sm font-medium">Lead *</label>
              {form.lead_id && (selectedLead || opp) ? (
                <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 font-medium">{selectedLead?.company?.name || opp?.lead_company_name}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setSelectedLead(null); setForm({ ...form, lead_id: '', contact_id: '' }) }}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        placeholder="Buscar lead por empresa..."
                        value={leadSearch}
                        onChange={(e) => { setLeadSearch(e.target.value); setShowLeadDropdown(true) }}
                        onFocus={() => setShowLeadDropdown(true)}
                      />
                      {showLeadDropdown && leads.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full bg-card border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {leads.map((lead) => (
                            <button
                              key={lead.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                              onClick={() => handleSelectLead(lead)}
                            >
                              <Building2 className="h-3 w-3 text-muted-foreground" />
                              <span className="font-medium">{lead.company?.name || 'Sem empresa'}</span>
                              <span className="text-muted-foreground text-xs ml-auto">{lead.primary_contact?.display_name || ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={() => window.open('/leads', '_blank')}>
                      <Plus className="h-4 w-4 mr-1" />Novo Lead
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Selection */}
          <div>
            <label className="text-sm font-medium">Contato da Oportunidade</label>
            <div className="flex gap-2">
              <select className={selectClass} value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}>
                <option value="">Selecione um contato</option>
                {contacts?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name || ''} {c.job_title ? `(${c.job_title})` : ''} {c.is_primary ? '★' : ''}
                  </option>
                ))}
              </select>
              {(selectedLead?.company_id || opp?.lead_company_id) && (
                <Button type="button" variant="outline" onClick={() => setShowContactForm(!showContactForm)}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Inline Contact Form */}
          {showContactForm && (
            <div className="p-3 rounded-lg border bg-muted/10 space-y-3">
              <h4 className="text-sm font-medium">Novo Contato</h4>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Nome *" value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} />
                <Input placeholder="Sobrenome" value={contactForm.last_name || ''} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} />
                <Input placeholder="Email" value={contactForm.email || ''} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                <Input placeholder="Telefone" value={contactForm.phone || ''} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
                <Input placeholder="Cargo" value={contactForm.job_title || ''} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} />
                <select className={selectClass} value={contactForm.role || ''} onChange={(e) => setContactForm({ ...contactForm, role: (e.target.value || null) as ContactRole | null })}>
                  <option value="">Papel</option>
                  {Object.entries(CONTACT_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })} className="h-4 w-4 rounded" />
                Contato principal da oportunidade
              </label>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={handleCreateContact} isLoading={createContactMutation.isPending}>Salvar Contato</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowContactForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {/* Opportunity Name */}
          <div>
            <label className="text-sm font-medium">Nome da Oportunidade *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Implantacao ERP - Empresa X" />
          </div>

          {/* Product / Value / Probability */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">Produto/Servico</label>
              <select className={selectClass} value={form.product_text} onChange={(e) => {
                setForm({ ...form, product_text: e.target.value })
                // Auto-update name if empty
                if (!form.name && e.target.value) {
                  const item = catalogItems?.find(i => i.id === e.target.value)
                  if (item && selectedLead?.company?.name) {
                    setForm(prev => ({ ...prev, product_text: e.target.value, name: `${item.name} - ${selectedLead.company?.name}` }))
                  }
                }
              }}>
                <option value="">Selecione</option>
                {catalogItems?.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.category})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Valor Estimado (R$) *</label>
              <Input type="number" step="0.01" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="100000.00" />
            </div>
            <div>
              <label className="text-sm font-medium">Probabilidade (%)</label>
              <select className={selectClass} value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })}>
                <option value="10">10%</option>
                <option value="25">25%</option>
                <option value="50">50%</option>
                <option value="75">75%</option>
                <option value="90">90%</option>
                <option value="100">100%</option>
              </select>
            </div>
          </div>

          {/* Close Date + Stage + Owner */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">Data Prevista</label>
              <Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">{isEdit ? 'Status' : 'Stage Inicial'}</label>
              <select className={selectClass} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value as OpportunityStage })}>
                {(isEdit
                  ? (Object.keys(STAGE_CONFIG) as OpportunityStage[])
                  : [...ACTIVE_STAGES, 'won' as OpportunityStage]
                ).map((s) => <option key={s} value={s}>{STAGE_CONFIG[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Responsavel</label>
              {canChangeOwner ? (
                <select className={selectClass} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              ) : (
                <div className="flex h-10 items-center px-3 rounded-md border border-input bg-muted/50 text-sm">
                  {currentUser?.full_name || '-'}
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending || moveStageMutation.isPending}>
              {isEdit ? 'Salvar' : 'Criar Oportunidade'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ==========================================
// Detail Modal
// ==========================================

function OppDetailModal({ opp, onClose }: { opp: Opportunity; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-xl mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <h2 className="text-lg font-semibold">Detalhes da Oportunidade</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-xl font-bold">{opp.name}</h3>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              {opp.lead_company_name || 'Sem empresa'}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Stage</h4>
              <Badge variant={STAGE_CONFIG[opp.stage]?.variant}>{STAGE_CONFIG[opp.stage]?.label}</Badge>
            </div>
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Valor</h4>
              <span className="text-lg font-bold">{formatCurrency(opp.value)}</span>
            </div>
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Probabilidade</h4>
              <span className="text-lg font-bold">{opp.probability}%</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Responsavel</h4>
              <span className="text-sm">{opp.owner_name || '-'}</span>
            </div>
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Contato</h4>
              <span className="text-sm">{opp.contact_name || '-'}</span>
            </div>
          </div>

          {opp.expected_close_date && (
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Data Prevista de Fechamento</h4>
              <span className="text-sm">{formatDate(opp.expected_close_date)}</span>
            </div>
          )}

          {opp.products && opp.products.length > 0 && (
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Produtos/Servicos</h4>
              <div className="flex flex-wrap gap-2">
                {opp.products.map((p, i) => <Badge key={i} variant="outline">{p.name || p.product_id}</Badge>)}
              </div>
            </div>
          )}

          {opp.loss_reason && (
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Motivo da Perda</h4>
              <span className="text-sm text-destructive">{opp.loss_reason}</span>
              {opp.loss_details && <p className="text-sm text-muted-foreground mt-1">{opp.loss_details}</p>}
            </div>
          )}

          {opp.proposals_count > 0 && (
            <div>
              <h4 className="font-semibold text-xs text-muted-foreground mb-1">Propostas</h4>
              <span className="text-sm">{opp.proposals_count} proposta(s) vinculada(s)</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground pt-2 border-t">
            <div>Criado: {formatDate(opp.created_at)}</div>
            <div>Atualizado: {formatDate(opp.updated_at)}</div>
            {opp.won_at && <div>Ganho em: {formatDate(opp.won_at)}</div>}
            {opp.lost_at && <div>Perdido em: {formatDate(opp.lost_at)}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Delete Modal
// ==========================================

function DeleteOppModal({ opp, onClose }: { opp: Opportunity; onClose: () => void }) {
  const mutation = useDeleteOpportunity()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const handleDelete = async () => {
    setBusy(true)
    try {
      // Backend faz exclusao em 2 passos: 1a chamada num registro nao-perdido
      // faz soft delete (marca 'lost'); a 2a, num registro ja 'lost', remove em
      // definitivo com cascade (propostas, atividades, tarefas). Encadeamos as
      // duas para que "Excluir" remova de fato numa unica acao do usuario.
      await mutation.mutateAsync(opp.id)
      if (opp.stage !== 'lost') {
        await mutation.mutateAsync(opp.id)
      }
      onClose()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 text-center">
          <Trash2 className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="font-semibold text-lg">Excluir Oportunidade?</h3>
          <p className="text-sm text-muted-foreground mt-2">
            "{opp.name}" sera <strong className="text-destructive">excluida permanentemente</strong>, junto com propostas, atividades e tarefas vinculadas. Esta acao nao pode ser desfeita.
          </p>
          {error && <div className="p-2 mt-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          <div className="flex justify-center gap-3 mt-6">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={busy || mutation.isPending}>
              Excluir permanentemente
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
